import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PRODUCTS } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Heart, Send, Check, Plus, Minus, ChevronRight, ShieldCheck, Truck, Award } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ProductImage from '../components/ProductImage';
import Seo, { SITE_URL } from '../components/Seo';

const ProductDetail = () => {
  const { id } = useParams();
  const product = PRODUCTS.find(p => p.id === id);
  const { addToCart, wishlist, toggleWishlist } = useCart();
  const [qty, setQty] = useState(1);

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <Seo title="Produkt nie znaleziony | AlphaPro Genetics" description="Produkt nie jest dostępny w katalogu AlphaPro Genetics." path={`/produkt/${id}`} noIndex />
        <h2 className="text-2xl font-bold text-slate-900">Produkt nie znaleziony</h2>
        <Link to="/sklep" className="mt-4 inline-block text-blue-600 hover:underline">Wróć do sklepu</Link>
      </div>
    );
  }

  const related = PRODUCTS.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  const liked = wishlist.includes(product.id);
  const productPath = `/produkt/${product.id}`;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <Seo
        title={`${product.name} | AlphaPro Genetics`}
        description={`${product.name} — ${product.short} Zobacz opis produktu w katalogu AlphaPro Genetics.`}
        path={productPath}
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Product',
              name: product.name,
              description: product.short,
              image: [product.image],
              sku: product.id,
              brand: { '@type': 'Brand', name: 'AlphaPro Genetics' },
              url: `${SITE_URL}${productPath}`,
              mainEntityOfPage: `${SITE_URL}${productPath}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Strona główna', item: SITE_URL },
                { '@type': 'ListItem', position: 2, name: 'Sklep', item: `${SITE_URL}/sklep` },
                { '@type': 'ListItem', position: 3, name: product.name, item: `${SITE_URL}${productPath}` },
              ],
            },
          ],
        }}
      />
      <nav className="flex items-center text-xs text-slate-500 mb-6">
        <Link to="/" className="hover:text-blue-600">Strona Główna</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <Link to="/sklep" className="hover:text-blue-600">Sklep</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <span className="text-blue-600 font-semibold truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 mb-16">
        {/* Image */}
        <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-3xl p-10 flex items-center justify-center min-h-[500px] border border-blue-100 relative overflow-hidden">
          {product.badge && (
            <span className="absolute top-5 left-5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold uppercase tracking-wider z-10 shadow-md shadow-blue-200">
              {product.badge}
            </span>
          )}
          <ProductImage
            src={product.image}
            alt={product.name}
            name={product.name}
            color={product.color}
            className="w-full max-w-md h-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
          />
        </div>

        {/* Info */}
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">{product.dosage}</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 tracking-tight">{product.name}</h1>
          <p className="text-slate-600 mb-8 leading-relaxed text-lg">{product.short}</p>

          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div className="flex items-center border border-blue-200 rounded-full bg-white">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-full"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-semibold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-full"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={() => addToCart(product, qty)} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-7 py-3 rounded-full font-semibold shadow-lg shadow-blue-200 transition">
              <ShoppingCart className="w-4 h-4" /> Dodaj do koszyka
            </button>
            <button onClick={() => toggleWishlist(product.id)} className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition ${liked ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-blue-200 text-slate-500 hover:border-blue-400'}`}>
              <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
            </button>
          </div>

          <a href="https://t.me/+9A5CpRihCfNjMzc0" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:underline mb-8">
            <Send className="w-4 h-4" /> Skontaktuj się przez Telegram
          </a>

          <div className="bg-blue-50/50 rounded-2xl p-6 border border-blue-100 mb-6">
            <h3 className="font-bold text-slate-900 mb-3">Opis produktu</h3>
            <p className="text-slate-700 leading-relaxed text-sm">{product.description}</p>
          </div>

          <div className="bg-white rounded-2xl border border-blue-100 p-6 mb-6">
            <h3 className="font-bold text-slate-900 mb-3">Kluczowe korzyści</h3>
            <ul className="space-y-2">
              {product.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-blue-600" />
                  </div>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-6 border-t border-blue-100">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              <span className="text-xs text-slate-600">Oryginalny produkt</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              <span className="text-xs text-slate-600">100% Anonimowość</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              <span className="text-xs text-slate-600">Wysyłka 24-48h</span>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6 tracking-tight">Podobne produkty</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
